import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

import ProductList from "../components/ProductList";
import CategoryList from "../components/CategoryList";
import BestSellerProducts from "../components/BestSellerProducts";
import LatestProducts from "../components/LatestProducts";
import QuickDiagnosticTool from "../components/QuickDiagnosticTool";
import MissionStatement from "../components/MissionStatement";
import VehicleList from "../components/VehicleList";
import ArticlesPage from "./ArticlesPage";

const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
];

const Homepage = () => {
  const { lang } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeAccordion, setActiveAccordion] = useState(null);

  const images = [
    "https://proprimemart.com/cdn-cgi/image/width=800,quality=75/lexus3505.jpg",
    "https://proprimemart.com/cdn-cgi/image/width=800,quality=75/lexus3506.jpg",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 30000);
    return () => clearInterval(interval);
  }, [images.length]);

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    document.body.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang, i18n]);

  const switchLanguage = (code) => {
    navigate(`/cm/${code}`);
  };

  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const faqItems = [
    {
      question: t("faq.q1"),
      answer: t("faq.a1"),
    },
    {
      question: t("faq.q2"),
      answer: t("faq.a2"),
    },
    {
      question: t("faq.q3"),
      answer: t("faq.a3"),
    },
    {
      question: t("faq.q4"),
      answer: t("faq.a4"),
    },
  ];

  return (
    <div className="homepage">
      <Helmet>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <link rel="canonical" href='https://237mechanic.com/cm/en' />
      </Helmet>

      {/* Language Switcher */}
      <div className="flex justify-center py-4">
        <select
          value={lang}
          onChange={(e) => switchLanguage(e.target.value)}
          className="border px-4 py-2 rounded-md shadow-sm text-gray-800 bg-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          {LANGUAGES.map(({ code, label, flag }) => (
            <option key={code} value={code}>
              {flag} {label}
            </option>
          ))}
        </select>
      </div>

      {/* Hero Slider */}
      <div className="relative w-full h-80 overflow-hidden">
        <div
          className="absolute inset-0 flex transition-transform duration-1000"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {images.map((src, index) => (
            <div
              key={index}
              className="relative w-full h-80 flex-shrink-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${src})` }}
            >
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
                <button
                  className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md shadow-md hover:bg-blue-700"
                  onClick={() => {
                    window.location.href = `/collections/brand-new-cars`;
                  }}
                >
                  {t("hero.cta")}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Slider Arrows */}
        <button
          className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full"
          onClick={() =>
            setCurrentSlide((prev) => (prev - 1 + images.length) % images.length)
          }
        >
          &#8249;
        </button>
        <button
          className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-gray-800 text-white p-2 rounded-full"
          onClick={() => setCurrentSlide((prev) => (prev + 1) % images.length)}
        >
          &#8250;
        </button>
      </div>

      <MissionStatement />
      <CategoryList />
      <QuickDiagnosticTool />
      <VehicleList categorySlug="brand-new-cars" />
      <ArticlesPage />

      {/* FAQ */}
      <div className="container mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-center mb-6">
          {t("faq.title")}
        </h2>
        <div>
          {faqItems.map((faq, index) => (
            <div key={index} className="border border-gray-300 rounded-md mb-4">
              <button
                onClick={() => toggleAccordion(index)}
                className="flex justify-between w-full p-4 text-lg font-semibold focus:outline-none"
              >
                {faq.question}
                <span>{activeAccordion === index ? "-" : "+"}</span>
              </button>
              {activeAccordion === index && (
                <div className="p-4 text-gray-600">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Homepage;
